import React from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'premium';
  size?: 'sm' | 'md' | 'lg' | 'icon';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', ...props }, ref) => {
    const variants = {
      primary: 'bg-cta text-cta-foreground hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_0px_rgba(26,60,52,1)] border-2 border-cta rounded-none',
      secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 border-2 border-transparent rounded-none',
      outline: 'bg-white/50 backdrop-blur-sm border border-primary/20 text-primary hover:bg-white hover:border-primary/50 hover:shadow-md hover:scale-[1.02] rounded-xl',
      ghost: 'hover:bg-secondary/10 hover:text-secondary-foreground border border-transparent rounded-xl',
      destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 border-2 border-destructive rounded-xl',
      premium: 'bg-cta text-cta-foreground border border-cta/20 shadow-[0_4px_14px_0_rgba(217,154,0,0.39)] hover:shadow-[0_6px_20px_rgba(217,154,0,0.23)] hover:scale-[1.02] active:scale-[0.98] rounded-xl bg-gradient-to-b from-cta/90 to-cta',
    };

    const sizes = {
      sm: 'h-11 px-4 text-sm',
      md: 'h-12 px-6 py-2 text-sm',
      lg: 'h-14 px-8 text-base',
      icon: 'h-10 w-10',
    };

    return (
      <button
        className={cn(
          'inline-flex items-center justify-center font-bold tracking-wide transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 select-none',
          variants[variant],
          sizes[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';
